/**
 * /admin/users — صفحة الأدمن لإدارة العملاء.
 *
 * تتيح: بحث + ملف ٣٦٠° + ٥ عمليّات (admin role / tier السفير /
 * override الشريحة / نقاط ولاء / محفظة) + سجلّ تعديلات كامل.
 */
import * as React from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Search, Loader2, ShieldCheck, ShieldAlert, Star, Heart, Coins, Wallet,
  History, Mail, Phone, Calendar, Crown, ChevronLeft, ChevronRight, KeyRound, UserPlus,
  Copy, CheckCircle2, AlertTriangle, MailWarning, MailCheck, RefreshCw, X,
  ShoppingBag, MapPin, TrendingUp, XCircle, ExternalLink, Gift, Users, Radio,
} from "lucide-react";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/lib/toast";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useLocale } from "@/contexts/LocaleContext";
import { t } from "@/lib/i18n";
import { SiteShell } from "@/components/SiteShell";
import { AdminNav } from "@/components/admin/AdminNav";
import { AdminHelpBox } from "@/components/admin/AdminHelpBox";
import { AdminUserActionDialog } from "@/components/admin/AdminUserActionDialog";
import { UsersTable } from "@/components/admin/UsersTable";
import { UsersSummaryCards } from "@/components/admin/UsersSummaryCards";
import { VisitorsPanel } from "@/components/admin/VisitorsPanel";


import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  searchUsersAdmin,
  listUsersAdmin,
  getUserDetailsAdmin,
  listUserAuditLog,
  grantUserRole,
  setAmbassadorTier,
  // setSegmentOverride removed — segment system deprecated
  grantLoyaltyPoints,
  adjustWalletCredit,
  adminConfirmUserEmail,
  adminGenerateRecoveryLink,
  adminCreateUser,
  createTrialRecipientAdmin,
} from "@/utils/adminUsers.functions";

export const Route = createFileRoute("/admin/users")({
  component: AdminUsersPage,
  validateSearch: (search: Record<string, unknown>) => ({
    user: typeof search.user === "string" ? search.user : undefined,
  }),
  head: () => ({
    meta: [{ title: "Users · Lecmo Admin" }, { name: "robots", content: "noindex" }],
  }),
});

import { TIERS, TIER_LABEL, type Tier } from "@/lib/loyalty/labels";
import { CustomerCartPanel } from "@/components/admin/CustomerCartPanel";

function AdminUsersPage() {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useIsAdmin();
  const navigate = useNavigate();
  const { locale } = useLocale();
  const isAr = locale === "ar";

  React.useEffect(() => {
    if (!authLoading && !roleLoading && (!user || !isAdmin)) {
      navigate({ to: "/" });
    }
  }, [authLoading, roleLoading, user, isAdmin, navigate]);

  const search = Route.useSearch();
  const selectedUserId = search.user ?? null;
  const setSelectedUserId = React.useCallback(
    (id: string | null) => {
      navigate({
        to: "/admin/users",
        search: { user: id ?? undefined },
        replace: true,
      });
    },
    [navigate],
  );

  if (authLoading || roleLoading || !isAdmin) {
    return (
      <SiteShell>
        <div className="container py-12 text-center text-muted-foreground">
          <Loader2 className="mx-auto h-6 w-6 animate-spin" />
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
        <AdminNav />
        <header className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight">
            {isAr ? "إدارة العملاء" : "User Management"}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isAr
              ? "ابحث عن عميل لمنح/سحب admin، تعديل tier السفير، فرض شريحة، منح نقاط، أو تعديل المحفظة. كلّ تعديل يُسجَّل."
              : "Search a customer to grant/revoke admin, change ambassador tier, override segment, grant points, or adjust wallet. All actions are audited."}
          </p>
        </header>

        <AdminHelpBox title={isAr ? "كيف تعمل هذه الصفحة" : "How this page works"}>
          <p>
            {isAr
              ? "كلّ عمليّة تتطلّب سبباً (٣ أحرف على الأقلّ) ويُحفظ في سجلّ التعديلات. لا يمكنك سحب admin من نفسك. الحدّ الأقصى للنقاط/المحفظة في كلّ عمليّة: ١٠٠٠٠."
              : "Every action requires a reason (min 3 chars) and is logged. You can't revoke admin from yourself. Max 10,000 points/credit per action."}
          </p>
        </AdminHelpBox>

        {selectedUserId ? (
          <UserDetailsView
            userId={selectedUserId}
            onBack={() => {
              // العودة مباشرةً للصفحة السابقة (مثلاً /admin/orders)، وإلّا للبحث
              if (typeof window !== "undefined" && window.history.length > 1) {
                window.history.back();
                return;
              }
              setSelectedUserId(null);
            }}
          />

        ) : (
          <Tabs defaultValue="customers" className="w-full">
            <TabsList className="mb-4 rounded-none">
              <TabsTrigger value="customers" className="gap-1.5 rounded-none">
                <Users className="h-3.5 w-3.5" />
                {isAr ? "العملاء المسجّلون" : "Customers"}
              </TabsTrigger>
              <TabsTrigger value="visitors" className="gap-1.5 rounded-none">
                <Radio className="h-3.5 w-3.5" />
                {isAr ? "الزوّار والجلسات" : "Visitors & sessions"}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="customers" className="space-y-4">
              <UsersSummaryCards />
              <UserSearchView onPick={setSelectedUserId} />
            </TabsContent>

            <TabsContent value="visitors">
              <VisitorsPanel onPick={setSelectedUserId} />
            </TabsContent>
          </Tabs>
        )}


      </div>
    </SiteShell>
  );
}

// ─────────────────────────────────────────────
// Search
// ─────────────────────────────────────────────
function UserSearchView({ onPick }: { onPick: (id: string) => void }) {
  const { locale } = useLocale();
  const isAr = locale === "ar";
  const [query, setQuery] = React.useState("");
  const [submittedQuery, setSubmittedQuery] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [sort, setSort] = React.useState<"newest" | "oldest" | "last_sign_in">("newest");
  const perPage = 20;

  const searchFn = useServerFn(searchUsersAdmin);
  const listFn = useServerFn(listUsersAdmin);

  const isSearching = submittedQuery.length > 0;

  const searchQuery = useQuery({
    queryKey: ["admin-users-search", submittedQuery],
    enabled: isSearching,
    retry: false,
    queryFn: () => searchFn({ data: { query: submittedQuery, limit: 30 } }),
  });

  const listQuery = useQuery({
    queryKey: ["admin-users-list", page, perPage, sort],
    enabled: !isSearching,
    queryFn: () => listFn({ data: { page, perPage, sort } }),
  });

  const data = isSearching ? searchQuery.data : listQuery.data;
  const isFetching = isSearching ? searchQuery.isFetching : listQuery.isFetching;

  const [createOpen, setCreateOpen] = React.useState(false);

  const clearSearch = () => {
    setQuery("");
    setSubmittedQuery("");
  };

  const renderUserCard = (u: {
    id: string;
    display_name: string;
    email: string | null;
    phone: string | null;
    market: string | null;
    email_confirmed_at?: string | null;
    last_sign_in_at?: string | null;
  }) => (
    <button
      key={u.id}
      onClick={() => onPick(u.id)}
      className="text-start rounded-none border border-border bg-card p-4 hover:bg-accent transition-colors"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate flex items-center gap-2">
            {u.display_name}
            {"email_confirmed_at" in u && (
              u.email_confirmed_at ? (
                <Badge variant="outline" className="text-[10px] gap-1 border-emerald-500/40 text-emerald-600 dark:text-emerald-400">
                  <MailCheck className="h-3 w-3" />
                  {isAr ? "مؤكَّد" : "Verified"}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] gap-1 border-amber-500/40 text-amber-600 dark:text-amber-400">
                  <MailWarning className="h-3 w-3" />
                  {isAr ? "غير مؤكَّد" : "Unverified"}
                </Badge>
              )
            )}
          </div>
          <div className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 mt-1">
            {u.email && (
              <span className="inline-flex items-center gap-1">
                <Mail className="h-3 w-3" /> {u.email}
              </span>
            )}
            {u.phone && (
              <span className="inline-flex items-center gap-1">
                <Phone className="h-3 w-3" /> {u.phone}
              </span>
            )}
            {u.market && <Badge variant="outline" className="text-[10px]">{u.market}</Badge>}
            {"last_sign_in_at" in u && u.last_sign_in_at && (
              <span className="inline-flex items-center gap-1 text-[11px]">
                <Calendar className="h-3 w-3" />
                {isAr ? "آخر دخول: " : "Last sign-in: "}
                {new Date(u.last_sign_in_at).toLocaleDateString(isAr ? "ar" : "en")}
              </span>
            )}
          </div>
        </div>
      </div>
    </button>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setSubmittedQuery(query.trim());
          }}
          className="flex gap-2 flex-1 min-w-[260px]"
        >
          <div className="relative flex-1">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={isAr ? "إيميل / اسم / هاتف / كود سفير…" : "Email / name / phone / ambassador code…"}
              className="ps-9"
            />
          </div>
          <Button type="submit" disabled={!query.trim() || isFetching}>
            {isFetching && isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : (isAr ? "بحث" : "Search")}
          </Button>
          {isSearching && (
            <Button type="button" variant="outline" onClick={clearSearch}>
              <X className="h-4 w-4 me-1" />
              {isAr ? "كل المستخدمين" : "All users"}
            </Button>
          )}
        </form>
        <Button variant="outline" onClick={() => setCreateOpen(true)}>
          <UserPlus className="h-4 w-4 me-1" />
          {isAr ? "إنشاء مستخدم" : "Create user"}
        </Button>
      </div>

      <CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} />

      {/* عند عدم البحث: جدول مع فلاتر + مُحدّد أعمدة */}
      {!isSearching ? (
        <UsersTable onPick={onPick} />
      ) : (
        <>
          {isFetching && !data && (
            <Card className="p-6 text-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin inline-block me-2" />
              {isAr ? "جارِ التحميل…" : "Loading…"}
            </Card>
          )}

          {searchQuery.isError && (
            <Card role="alert" className="p-6 text-center text-destructive">
              {isAr
                ? "تعذّر إكمال البحث. هذا لا يعني أن الحساب غير موجود. حاول مجدداً أو ابحث بمعرّف المستخدم."
                : "Search could not be completed. This does not mean the account is missing. Try again or search by user ID."}
            </Card>
          )}

          {data && data.users.length === 0 && !isFetching && !searchQuery.isError && (
            <Card className="p-6 text-center text-muted-foreground">
              {isAr ? "لا توجد نتائج." : "No results."}
            </Card>
          )}

          <div className="grid gap-2">
            {!searchQuery.isError && data?.users.map((u) => renderUserCard(u))}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Details
// ─────────────────────────────────────────────
function UserDetailsView({ userId, onBack }: { userId: string; onBack: () => void }) {
  const { locale } = useLocale();
  const isAr = locale === "ar";
  const qc = useQueryClient();
  const { isStaffOnly, isSuperAdmin } = useIsAdmin();
  const detailsFn = useServerFn(getUserDetailsAdmin);
  const auditFn = useServerFn(listUserAuditLog);
  const [activeTab, setActiveTab] = React.useState<string>(isStaffOnly ? "overview" : "actions");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-user-details", userId],
    queryFn: () => detailsFn({ data: { userId } }),
  });
  const { data: auditData } = useQuery({
    queryKey: ["admin-user-audit", userId],
    queryFn: () => auditFn({ data: { userId } }),
  });

  const refetch = () => {
    qc.invalidateQueries({ queryKey: ["admin-user-details", userId] });
    qc.invalidateQueries({ queryKey: ["admin-user-audit", userId] });
  };

  if (isLoading || !data) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <Loader2 className="mx-auto h-6 w-6 animate-spin" />
      </div>
    );
  }

  const userRoles = data.roles ?? [];
  const isUserSuperAdmin = userRoles.includes("super_admin");
  const isUserAdmin = userRoles.includes("admin");
  const isUserStaff = userRoles.includes("staff");
  // segment system removed

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={onBack}>
        <ChevronLeft className={isAr ? "rotate-180 me-1 h-4 w-4" : "me-1 h-4 w-4"} />
        {isAr ? "العودة للصفحة السابقة" : "Back"}
      </Button>

      {/* Header */}
      {(() => {
        // ـــ سلسلة بدائل للهاتف ـــ
        const phoneFromProfile = data.profile?.phone_e164 ?? null;
        const phoneFromAuth = (data as { authPhone?: string | null }).authPhone ?? null;
        const phoneFromOrder = data.orders?.find((o) => (o as { customer_phone?: string | null }).customer_phone)?.customer_phone ?? null;
        const resolvedPhone = phoneFromProfile || phoneFromAuth || phoneFromOrder || null;
        const phoneSource: "profile" | "auth" | "order" | null =
          phoneFromProfile ? "profile" : phoneFromAuth ? "auth" : phoneFromOrder ? "order" : null;
        const phoneSourceLabel = phoneSource === "auth"
          ? (isAr ? "من تسجيل الدخول" : "from sign-up")
          : phoneSource === "order"
          ? (isAr ? "من آخر طلب" : "from last order")
          : null;

        const country = data.profile?.self_country ?? data.profile?.last_ip_country ?? null;
        const market = data.profile?.preferred_market ?? null;
        const lastShip = data.orders?.[0]?.shipping_city || data.orders?.[0]?.shipping_country || null;

        // صفّ موحَّد label/value
        const Row = ({ icon: Icon, label, children }: { icon: React.ComponentType<{ className?: string }>; label: string; children: React.ReactNode }) => (
          <div className="grid grid-cols-[88px_1fr] items-start gap-2 py-1 text-sm sm:grid-cols-[110px_1fr]">
            <div className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Icon className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
            </div>
            <div className="min-w-0 break-words text-foreground">{children}</div>
          </div>
        );
        const Missing = () => (
          <span className="inline-flex items-center gap-1 rounded-none bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            {isAr ? "غير متوفّر" : "Not provided"}
          </span>
        );

        return (
      <Card className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="min-w-0">
            <h2 className="text-xl font-bold">{data.profile?.display_name ?? (isAr ? "بدون اسم" : "No name")}</h2>
          </div>
          <div className="flex gap-2 flex-wrap">
            {isUserSuperAdmin && <Badge className="bg-destructive">Super Admin</Badge>}
            {!isUserSuperAdmin && isUserAdmin && <Badge className="bg-destructive">{isAr ? "أدمن" : "Admin"}</Badge>}
            {!isUserSuperAdmin && !isUserAdmin && isUserStaff && <Badge variant="secondary">{isAr ? "موظّف" : "Staff"}</Badge>}
            {data.ambassador && (
              <Badge variant="outline" className="gap-1.5">
                {isAr ? "سفير" : "Ambassador"} · {TIER_LABEL[data.ambassador.tier as Tier][isAr ? "ar" : "en"]}
                {data.ambassador.code && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(data.ambassador!.code!);
                      toast.success(isAr ? "نُسخ كود السفير" : "Ambassador code copied");
                    }}
                    className="ms-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-muted hover:bg-accent font-mono text-[11px] tracking-wider"
                    title={isAr ? "انقر للنسخ" : "Click to copy"}
                  >
                    {data.ambassador.code}
                    <Copy className="h-2.5 w-2.5" />
                  </button>
                )}
              </Badge>
            )}
          </div>
        </div>

        {/* بيانات الاتصال — صفوف منظَّمة */}
        <div className="divide-y divide-border/50">
          <Row icon={Mail} label={isAr ? "الإيميل" : "Email"}>
            {data.email ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm break-all">{data.email}</span>
                {data.emailConfirmedAt ? (
                  <span className="inline-flex items-center gap-0.5 rounded-none bg-emerald-500/10 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400">
                    <MailCheck className="h-3 w-3" />{isAr ? "مؤكَّد" : "Verified"}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-0.5 rounded-none bg-destructive/10 px-1.5 py-0.5 text-[11px] font-medium text-destructive">
                    <MailWarning className="h-3 w-3" />{isAr ? "غير مؤكَّد" : "Unverified"}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(data.email!);
                    toast.success(isAr ? "نُسخ" : "Copied");
                  }}
                  className="text-muted-foreground hover:text-foreground"
                  title={isAr ? "نسخ" : "Copy"}
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : <Missing />}
          </Row>

          <Row icon={Phone} label={isAr ? "الهاتف" : "Phone"}>
            {resolvedPhone ? (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm" dir="ltr">{resolvedPhone}</span>
                {phoneSourceLabel && (
                  <span className="inline-flex items-center rounded-none bg-amber-500/10 px-1.5 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                    {phoneSourceLabel}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard.writeText(resolvedPhone);
                    toast.success(isAr ? "نُسخ" : "Copied");
                  }}
                  className="text-muted-foreground hover:text-foreground"
                  title={isAr ? "نسخ" : "Copy"}
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <a
                  href={`https://wa.me/${resolvedPhone.replace(/[^\d]/g, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-none bg-emerald-500/10 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20"
                >
                  <Phone className="h-3 w-3" />WhatsApp
                </a>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Missing />
                <span className="text-xs text-muted-foreground">{isAr ? "لم يُسجَّل أثناء التسجيل ولا في الطلبات" : "Not collected at sign-up or in orders"}</span>
              </div>
            )}
          </Row>

          <Row icon={MapPin} label={isAr ? "البلد / السوق" : "Country / Market"}>
            {(country || market || lastShip) ? (
              <div className="flex flex-col gap-0.5 text-sm">
                {country && <div>{country}{data.profile?.self_country ? "" : <span className="text-xs text-muted-foreground"> ({isAr ? "تقديريّ من IP" : "from IP"})</span>}</div>}
                {market && <div className="text-xs text-muted-foreground">{isAr ? "السوق المفضّل" : "Preferred market"}: <span className="font-mono">{market}</span></div>}
                {lastShip && <div className="text-xs text-muted-foreground">{isAr ? "آخر شحن" : "Last ship"}: {lastShip}</div>}
              </div>
            ) : <Missing />}
          </Row>

          {data.profile?.created_at && (
            <Row icon={Calendar} label={isAr ? "الانضمام" : "Joined"}>
              <span className="text-sm">{new Date(data.profile.created_at).toLocaleDateString(isAr ? "ar" : "en")}</span>
            </Row>
          )}

          {data.lastSignInAt && (
            <Row icon={History} label={isAr ? "آخر دخول" : "Last sign in"}>
              <span className="text-sm">{new Date(data.lastSignInAt).toLocaleDateString(isAr ? "ar" : "en")}</span>
            </Row>
          )}
        </div>

        {/* أزرار سريعة */}
        <div className="mt-4 pt-3 border-t border-border/50 flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const lines = [
                data.profile?.display_name && `${isAr ? "الاسم" : "Name"}: ${data.profile.display_name}`,
                data.email && `${isAr ? "الإيميل" : "Email"}: ${data.email}`,
                resolvedPhone && `${isAr ? "الهاتف" : "Phone"}: ${resolvedPhone}`,
                data.profile?.preferred_market && `${isAr ? "السوق" : "Market"}: ${data.profile.preferred_market}`,
              ].filter(Boolean).join("\n");
              navigator.clipboard.writeText(lines);
              toast.success(isAr ? "نُسخت بيانات الاتصال" : "Contact info copied");
            }}
          >
            <Copy className="h-3.5 w-3.5 me-1.5" />
            {isAr ? "نسخ كلّ البيانات" : "Copy all info"}
          </Button>
          {data.email && (
            <Button size="sm" variant="outline" asChild>
              <a href={`mailto:${data.email}`}>
                <Mail className="h-3.5 w-3.5 me-1.5" />{isAr ? "أرسل إيميل" : "Send email"}
              </a>
            </Button>
          )}
          {resolvedPhone && (
            <Button size="sm" variant="outline" asChild className="text-emerald-700 dark:text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10">
              <a href={`https://wa.me/${resolvedPhone.replace(/[^\d]/g, "")}`} target="_blank" rel="noopener noreferrer">
                <Phone className="h-3.5 w-3.5 me-1.5" />{isAr ? "افتح واتساب" : "Open WhatsApp"}
              </a>
            </Button>
          )}
        </div>
      </Card>
        );
      })()}

      {/* بانر تنبيهات حالة الحساب */}
      <UserAlertsBanner
        isAr={isAr}
        emailConfirmed={!!data.emailConfirmedAt}
        lastSignInAt={data.lastSignInAt}
        hasPhone={!!data.profile?.phone_e164}
      />

      {/* شريط KPIs — قيمة العميل في ١٠ ثوانٍ */}
      <KpiStrip
        isAr={isAr}
        kpis={data.kpis}
        loyalty={data.loyalty}
        balances={data.balances ?? []}
        currency={data.orders?.[0]?.currency ?? "AED"}
        walletTxns={data.walletTxns ?? []}
        onJumpToTab={setActiveTab}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap h-auto">
          {!isStaffOnly && (
            <TabsTrigger value="actions">{isAr ? "الإجراءات" : "Actions"}</TabsTrigger>
          )}
          <TabsTrigger value="overview">{isAr ? "نظرة عامّة" : "Overview"}</TabsTrigger>
          <TabsTrigger value="orders">
            {isAr ? "الطلبات" : "Orders"} ({data.kpis?.totalOrders ?? 0})
          </TabsTrigger>
          <TabsTrigger value="addresses">
            {isAr ? "العناوين" : "Addresses"} ({data.recipients?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="favorites">
            {isAr ? "المفضّلة" : "Favorites"} ({data.wishlist?.length ?? 0})
          </TabsTrigger>
          <TabsTrigger value="wallet">
            {isAr ? "المحفظة" : "Wallet"} ({(data.loyaltyTxns?.length ?? 0) + (data.walletTxns?.length ?? 0)})
          </TabsTrigger>
          <TabsTrigger value="activity">
            {isAr ? "النشاط" : "Activity"} ({(data.events?.length ?? 0) + (data.ambassadorEvents?.length ?? 0)})
          </TabsTrigger>

          {!isStaffOnly && (
            <TabsTrigger value="audit">
              {isAr ? "سجلّ التعديلات" : "Audit"} ({auditData?.rows.length ?? 0})
            </TabsTrigger>
          )}
        </TabsList>


        {!isStaffOnly && (
          <TabsContent value="actions" className="space-y-4 mt-4">
            <div className="grid gap-4 md:grid-cols-2">
              {(isUserSuperAdmin || isUserAdmin || isUserStaff) ? (
                <Card className="p-4 md:col-span-2 border-amber-300 bg-amber-50/40 dark:bg-amber-950/10">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-sm">
                      <div className="font-semibold flex items-center gap-1.5">
                        <ShieldCheck className="h-4 w-4 text-amber-600" />
                        {isAr ? "عضو فريق" : "Team member"}
                      </div>
                      <div className="text-muted-foreground text-xs mt-0.5">
                        {isAr
                          ? "هذا المستخدم يملك صلاحيّات داخليّة. لإدارة الأدوار اذهب إلى صفحة الفريق."
                          : "This user has internal access. Manage roles in the Team page."}
                      </div>
                    </div>
                    <Button asChild size="sm" variant="outline">
                      <Link to="/admin/team">{isAr ? "صفحة الفريق" : "Open Team"}</Link>
                    </Button>
                  </div>
                </Card>
              ) : isSuperAdmin ? (
                <Card className="p-3 md:col-span-2 border-dashed">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5">
                      <ShieldCheck className="h-3.5 w-3.5" />
                      {isAr
                        ? "عميل عاديّ بلا صلاحيّات داخليّة."
                        : "Regular customer — no internal permissions."}
                    </div>
                    <GrantTeamRoleButton userId={userId} isAr={isAr} onDone={refetch} />
                  </div>
                </Card>
              ) : null}
              <TierCard
                userId={userId}
                hasAmbassador={!!data.ambassador}
                currentTier={data.ambassador?.tier as Tier | undefined}
                currentStatus={data.ambassador?.status}
                onDone={refetch}
              />
              {/* SegmentCard removed — segment system deprecated */}
              <LoyaltyCard
                userId={userId}
                balance={data.loyalty.points_balance}
                lifetime={data.loyalty.lifetime_earned}
                onDone={refetch}
              />
              <WalletCard
                userId={userId}
                balances={data.balances}
                hasAmbassador={!!data.ambassador}
                onDone={refetch}
              />
              <AccessCard
                userId={userId}
                email={data.email}
                onDone={refetch}
              />
            </div>
          </TabsContent>
        )}

        <TabsContent value="overview" className="mt-4 space-y-4">
          <Card className="p-4">
            <h3 className="font-semibold mb-3">{isAr ? "آخر الطلبات" : "Recent Orders"}</h3>
            {data.recentOrders.length === 0 ? (
              <p className="text-sm text-muted-foreground">{isAr ? "لا طلبات." : "No orders."}</p>
            ) : (
              <ul className="text-sm space-y-2">
                {data.recentOrders.map((o) => (
                  <li key={o.id} className="flex justify-between border-b border-border/50 pb-1.5">
                    <span>{o.order_name ?? o.id} {o.is_gift && "🎁"}</span>
                    <span className="text-muted-foreground">
                      {Number(o.total_amount ?? 0).toFixed(2)} {o.currency} · {o.financial_status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          {false && null}
        </TabsContent>

        <TabsContent value="orders" className="mt-4">
          <OrdersTab
            isAr={isAr}
            locale={locale}
            orders={data.orders ?? []}
            loyaltyTxns={data.loyaltyTxns ?? []}
            walletTxns={data.walletTxns ?? []}
          />
        </TabsContent>

        <TabsContent value="addresses" className="mt-4">
          <AddressesTab isAr={isAr} locale={locale} recipients={data.recipients ?? []} profile={data.profile} userId={data.profile?.id ?? ""} />
        </TabsContent>

        <TabsContent value="favorites" className="mt-4">
          <div className="space-y-4">
            <FavoritesTab isAr={isAr} wishlist={data.wishlist ?? []} />
            <Card className="p-4">
              <h3 className="mb-3 font-semibold">{isAr ? "السلّة" : "Cart"}</h3>
              <CustomerCartPanel userId={userId} isAr={isAr} />
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="wallet" className="mt-4">
          <WalletHistoryTab
            isAr={isAr}
            loyaltyTxns={data.loyaltyTxns ?? []}
            walletTxns={data.walletTxns ?? []}
          />
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card className="p-4">
              <h3 className="mb-3 font-semibold">
                {isAr ? "نشاط السفير" : "Ambassador activity"}
              </h3>
              {!data.ambassadorEvents?.length ? (
                <p className="text-sm text-muted-foreground">
                  {isAr ? "لا نقرات ولا مبيعات عبر كوده." : "No clicks or sales via their code."}
                </p>
              ) : (
                <ul className="space-y-1 text-sm">
                  {data.ambassadorEvents.map((e) => (
                    <li key={e.id} className="flex justify-between border-b border-border/40 pb-1">
                      <span>{e.event_type}</span>
                      <span className="text-xs text-muted-foreground">
                        {new Date(e.created_at as string).toLocaleString(isAr ? "ar" : "en-GB")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card className="p-4">
              <h3 className="mb-3 font-semibold">
                {isAr ? "آخر أحداث التصفّح" : "Recent browsing events"}
              </h3>
              {!data.events?.length ? (
                <p className="text-sm text-muted-foreground">
                  {isAr ? "لا أحداث مسجَّلة." : "No events recorded."}
                </p>
              ) : (
                <ul className="max-h-80 space-y-1 overflow-y-auto text-sm">
                  {data.events.map((e) => (
                    <li
                      key={e.id}
                      className="flex items-center justify-between gap-2 border-b border-border/40 pb-1"
                    >
                      <span>{e.event_type}</span>
                      <span className="truncate text-xs text-muted-foreground" dir="ltr">
                        {e.product_title ?? e.product_handle ?? e.path ?? ""}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground">
                        {new Date(e.created_at as string).toLocaleString(isAr ? "ar" : "en-GB")}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </TabsContent>

        {!isStaffOnly && (
          <TabsContent value="audit" className="mt-4">

            <Card className="p-4">
              {auditData?.rows.length === 0 ? (
                <p className="text-sm text-muted-foreground">{isAr ? "لا تعديلات." : "No actions yet."}</p>
              ) : (
                <ul className="space-y-3">
                  {auditData?.rows.map((row) => {
                    const meta = (row.metadata ?? {}) as { reason?: string };
                    return (
                      <li key={row.id} className="text-sm border-b border-border/50 pb-2 last:border-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="font-medium">{row.action}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(row.created_at).toLocaleString(isAr ? "ar" : "en")}
                          </span>
                        </div>
                        {meta.reason && (
                          <p className="text-muted-foreground mt-1">💬 {meta.reason}</p>
                        )}
                        {(row.before_state || row.after_state) && (
                          <pre className="text-[10px] bg-muted/30 rounded p-1.5 mt-1 overflow-x-auto">
                            {JSON.stringify({ before: row.before_state, after: row.after_state }, null, 0)}
                          </pre>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-none bg-muted/30 p-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Action Cards
// ─────────────────────────────────────────────
type ManagedRole = "super_admin" | "admin" | "staff";
// RolesCard removed — moved to /admin/team for cleaner separation between
// customer management and team/permissions management.

function TierCard({
  userId, hasAmbassador, currentTier, currentStatus, onDone,
}: {
  userId: string;
  hasAmbassador: boolean;
  currentTier?: Tier;
  currentStatus?: string;
  onDone: () => void;
}) {
  const { locale } = useLocale();
  const isAr = locale === "ar";
  const [open, setOpen] = React.useState(false);
  const [tier, setTier] = React.useState<Tier>(currentTier ?? "decouverte");
  const [status, setStatus] = React.useState<string>(currentStatus ?? "approved");
  const tierFn = useServerFn(setAmbassadorTier);

  React.useEffect(() => {
    setTier(currentTier ?? "decouverte");
    setStatus(currentStatus ?? "approved");
  }, [currentTier, currentStatus]);

  if (!hasAmbassador) {
    return (
      <Card className="p-4 space-y-2">
        <div className="flex items-center gap-2 font-semibold">
          <Star className="h-4 w-4 text-primary" />
          {isAr ? "السفير" : "Ambassador"}
        </div>
        <p className="text-xs text-muted-foreground">
          {isAr ? "لا حساب سفير لهذا المستخدم." : "No ambassador account for this user."}
        </p>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 font-semibold">
          <Star className="h-4 w-4 text-primary" />
          {isAr ? "السفير" : "Ambassador"}
        </div>
        <p className="text-xs text-muted-foreground">
          tier: <strong>{currentTier}</strong> · status: <strong>{currentStatus}</strong>
        </p>
        <Button size="sm" onClick={() => setOpen(true)}>
          {isAr ? "تعديل tier/status" : "Change tier/status"}
        </Button>
      </Card>
      <AdminUserActionDialog
        open={open}
        onOpenChange={setOpen}
        title={isAr ? "تعديل السفير" : "Edit ambassador"}
        onConfirm={async (reason) => {
          await tierFn({ data: { userId, tier, status: status as "pending" | "approved" | "rejected" | "suspended", reason } });
          toast.success(isAr ? "تمّ" : "Done");
          onDone();
        }}
      >
        <div className="space-y-3">
          <div>
            <Label>Tier</Label>
            <Select value={tier} onValueChange={(v) => setTier(v as Tier)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIERS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {TIER_LABEL[t][isAr ? "ar" : "en"]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">pending</SelectItem>
                <SelectItem value="approved">approved</SelectItem>
                <SelectItem value="rejected">rejected</SelectItem>
                <SelectItem value="suspended">suspended</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </AdminUserActionDialog>
    </>
  );
}

// SegmentCard removed — segment system deprecated (backend returns 410)

function LoyaltyCard({
  userId, balance, lifetime, onDone,
}: { userId: string; balance: number; lifetime: number; onDone: () => void }) {
  const { locale } = useLocale();
  const isAr = locale === "ar";
  const [open, setOpen] = React.useState(false);
  const [points, setPoints] = React.useState(100);
  const grantFn = useServerFn(grantLoyaltyPoints);

  return (
    <>
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 font-semibold">
          <Coins className="h-4 w-4 text-primary" />
          {isAr ? "نقاط الولاء" : "Loyalty Points"}
        </div>
        <p className="text-xs text-muted-foreground">
          {isAr ? "الرصيد" : "Balance"}: <strong>{balance}</strong> · {isAr ? "مدى الحياة" : "Lifetime"}: {lifetime}
        </p>
        <Button size="sm" onClick={() => setOpen(true)}>
          {isAr ? "منح/خصم نقاط" : "Grant/Deduct points"}
        </Button>
      </Card>
      <AdminUserActionDialog
        open={open}
        onOpenChange={setOpen}
        title={isAr ? "تعديل نقاط الولاء" : "Adjust loyalty points"}
        description={isAr
          ? "موجب = منح، سالب = خصم. الحدّ ±١٠٠٠٠."
          : "Positive = grant, negative = deduct. Range ±10,000."}
        onConfirm={async (reason) => {
          await grantFn({ data: { userId, points, reason } });
          toast.success(isAr ? `تمّ منح ${points} نقطة` : `Granted ${points} pts`);
          onDone();
        }}
      >
        <div>
          <Label>{isAr ? "النقاط" : "Points"}</Label>
          <Input
            type="number"
            min={-10000}
            max={10000}
            value={points}
            onChange={(e) => setPoints(Number(e.target.value))}
          />
        </div>
      </AdminUserActionDialog>
    </>
  );
}

function WalletCard({
  userId, balances, hasAmbassador, onDone,
}: {
  userId: string;
  balances: Array<{ currency: string; available_credit: number }>;
  hasAmbassador: boolean;
  onDone: () => void;
}) {
  const { locale } = useLocale();
  const isAr = locale === "ar";
  const [open, setOpen] = React.useState(false);
  const [amount, setAmount] = React.useState(0);
  const [currency, setCurrency] = React.useState("AED");
  const adjustFn = useServerFn(adjustWalletCredit);

  if (!hasAmbassador) {
    return (
      <Card className="p-4 space-y-2">
        <div className="flex items-center gap-2 font-semibold">
          <Wallet className="h-4 w-4 text-primary" />
          {isAr ? "محفظة السفير" : "Ambassador Wallet"}
        </div>
        <p className="text-xs text-muted-foreground">
          {isAr ? "لا حساب سفير." : "No ambassador account."}
        </p>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 font-semibold">
          <Wallet className="h-4 w-4 text-primary" />
          {isAr ? "محفظة السفير" : "Ambassador Wallet"}
        </div>
        <ul className="text-xs text-muted-foreground space-y-0.5">
          {balances.length === 0 && <li>—</li>}
          {balances.map((b) => (
            <li key={b.currency}>
              {b.currency}: <strong>{b.available_credit.toFixed(2)}</strong>
            </li>
          ))}
        </ul>
        <Button size="sm" onClick={() => setOpen(true)}>
          {isAr ? "تعديل الرصيد" : "Adjust balance"}
        </Button>
      </Card>
      <AdminUserActionDialog
        open={open}
        onOpenChange={setOpen}
        title={isAr ? "تعديل رصيد المحفظة" : "Adjust wallet balance"}
        description={isAr
          ? "موجب = إضافة، سالب = خصم. الحدّ ±١٠٠٠٠."
          : "Positive = credit, negative = debit. Range ±10,000."}
        onConfirm={async (reason) => {
          await adjustFn({ data: { userId, amount, currency, reason } });
          toast.success(isAr ? "تمّ التعديل" : "Adjusted");
          onDone();
        }}
      >
        <div className="space-y-3">
          <div>
            <Label>{isAr ? "المبلغ" : "Amount"}</Label>
            <Input
              type="number"
              step="0.01"
              min={-10000}
              max={10000}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
          </div>
          <div>
            <Label>{isAr ? "العملة" : "Currency"}</Label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="AED">AED</SelectItem>
                <SelectItem value="SAR">SAR</SelectItem>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="OMR">OMR</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </AdminUserActionDialog>
    </>
  );
}

// ─────────────────────────────────────────────
// User Alerts Banner — تنبيهات حالة الحساب
// ─────────────────────────────────────────────
function UserAlertsBanner({
  isAr, emailConfirmed, lastSignInAt, hasPhone,
}: {
  isAr: boolean;
  emailConfirmed: boolean;
  lastSignInAt: string | null;
  hasPhone: boolean;
}) {
  const alerts: Array<{ tone: "destructive" | "warning" | "info"; text: string }> = [];

  if (!emailConfirmed) {
    alerts.push({
      tone: "destructive",
      text: isAr
        ? "الإيميل غير مؤكَّد — لا يستطيع المستخدم تسجيل الدخول. استخدم «تأكيد الإيميل» أو «رابط الاسترداد» في كرت وصول الحساب."
        : "Email not verified — user cannot sign in. Use “Confirm Email” or “Recovery Link” in the Access card.",
    });
  }

  if (!lastSignInAt) {
    alerts.push({
      tone: "warning",
      text: isAr
        ? "لم يسجّل دخولاً مطلقاً — قد لم يستلم رابط الترحيب أو لم يكبسه."
        : "Never signed in — may not have received or clicked the welcome link.",
    });
  } else {
    const days = Math.floor((Date.now() - new Date(lastSignInAt).getTime()) / (1000 * 60 * 60 * 24));
    if (days >= 90) {
      alerts.push({
        tone: "info",
        text: isAr
          ? `آخر دخول قبل ${days} يوماً — حساب نائم.`
          : `Last sign-in ${days} days ago — dormant account.`,
      });
    }
  }

  if (!hasPhone) {
    alerts.push({
      tone: "warning",
      text: isAr
        ? "لا هاتف مسجَّل — التواصل عبر واتساب غير ممكن."
        : "No phone on file — WhatsApp contact unavailable.",
    });
  }

  if (alerts.length === 0) return null;

  const toneClass = (t: "destructive" | "warning" | "info") =>
    t === "destructive"
      ? "border-destructive/40 bg-destructive/5 text-destructive"
      : t === "warning"
      ? "border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-400"
      : "border-border bg-muted/30 text-muted-foreground";

  return (
    <div className="space-y-2">
      {alerts.map((a, i) => (
        <div
          key={i}
          className={`flex items-start gap-2 rounded-none border p-3 text-sm ${toneClass(a.tone)}`}
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{a.text}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// Account Access Card (manual confirm + recovery link)
// ─────────────────────────────────────────────
function AccessCard({
  userId, email, onDone,
}: { userId: string; email: string | null; onDone: () => void }) {
  const { locale } = useLocale();
  const isAr = locale === "ar";
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [linkOpen, setLinkOpen] = React.useState(false);
  const [linkResult, setLinkResult] = React.useState<{ url: string; type: string } | null>(null);
  const [copied, setCopied] = React.useState(false);
  const confirmFn = useServerFn(adminConfirmUserEmail);
  const linkFn = useServerFn(adminGenerateRecoveryLink);

  return (
    <>
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 font-semibold">
          <KeyRound className="h-4 w-4 text-primary" />
          {isAr ? "وصول الحساب" : "Account Access"}
        </div>
        <p className="text-xs text-muted-foreground">
          {isAr
            ? "إن لم يصل إيميل التأكيد للمستخدم، أكِّد يدويّاً أو ولّد رابط استرداد كلمة مرور لإرساله عبر واتساب."
            : "If verification email didn't arrive, confirm manually or generate a password-reset link to share via WhatsApp."}
        </p>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setConfirmOpen(true)} disabled={!email}>
            <CheckCircle2 className="h-4 w-4 me-1" />
            {isAr ? "تأكيد الإيميل" : "Confirm email"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setLinkOpen(true)} disabled={!email}>
            <KeyRound className="h-4 w-4 me-1" />
            {isAr ? "رابط استرداد" : "Recovery link"}
          </Button>
        </div>
        {!email && (
          <p className="text-xs text-destructive">
            {isAr ? "لا إيميل لهذا المستخدم." : "No email on this user."}
          </p>
        )}
      </Card>

      <AdminUserActionDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={isAr ? "تأكيد إيميل المستخدم يدويّاً" : "Manually confirm user email"}
        description={isAr
          ? "سيُسمح للمستخدم بتسجيل الدخول مباشرة دون الحاجة إلى تأكيد الإيميل."
          : "The user will be able to sign in immediately without email verification."}
        onConfirm={async (reason) => {
          const res = await confirmFn({ data: { userId, reason } });
          toast.success(
            res.alreadyConfirmed
              ? (isAr ? "كان مؤكَّداً مسبقاً" : "Already confirmed")
              : (isAr ? "تمّ التأكيد" : "Confirmed"),
          );
          onDone();
        }}
      />

      <RecoveryLinkDialog
        open={linkOpen}
        onOpenChange={(o) => { setLinkOpen(o); if (!o) { setLinkResult(null); setCopied(false); } }}
        result={linkResult}
        copied={copied}
        onCopy={() => {
          if (!linkResult) return;
          navigator.clipboard.writeText(linkResult.url);
          setCopied(true);
          toast.success(isAr ? "نُسخ" : "Copied");
        }}
        onGenerate={async (reason) => {
          const res = await linkFn({ data: { userId, type: "recovery", reason } });
          setLinkResult({ url: res.actionLink, type: res.type });
          toast.success(isAr ? "تمّ التوليد" : "Generated");
        }}
      />
    </>
  );
}

// ─────────────────────────────────────────────
// Create User Dialog
// ─────────────────────────────────────────────
function CreateUserDialog({
  open, onOpenChange,
}: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { locale } = useLocale();
  const isAr = locale === "ar";
  const [email, setEmail] = React.useState("");
  const [displayName, setDisplayName] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState<{ email: string; recoveryLink: string | null } | null>(null);
  const [copied, setCopied] = React.useState(false);
  const createFn = useServerFn(adminCreateUser);

  const reset = () => {
    setEmail(""); setDisplayName(""); setPhone(""); setReason("");
    setResult(null); setCopied(false); setBusy(false);
  };

  const handleClose = (o: boolean) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await createFn({
        data: { email: email.trim(), displayName: displayName.trim(), phone: phone.trim() || null, reason: reason.trim() },
      });
      setResult({ email: res.email, recoveryLink: res.recoveryLink });
      toast.success(isAr ? "تمّ إنشاء الحساب" : "User created");
    } catch (e) {
      const err = e as Error;
      toast.error(err.message || (isAr ? "فشل الإنشاء" : "Failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isAr ? "إنشاء مستخدم جديد" : "Create new user"}</DialogTitle>
          <DialogDescription>
            {isAr
              ? "سيُنشأ الحساب مع تأكيد إيميل تلقائيّ + رابط استرداد لإرساله للمستخدم."
              : "Account is created with auto-confirmed email + recovery link to share."}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <div className="rounded-none border border-border bg-muted/30 p-3 text-sm">
              <strong>{result.email}</strong>
              <p className="text-xs text-muted-foreground mt-1">
                {isAr ? "الحساب جاهز ومؤكَّد. أرسل الرابط أدناه ليضع كلمة مرور." : "Account is ready & confirmed. Share the link so they set a password."}
              </p>
            </div>
            {result.recoveryLink && (
              <div className="space-y-2">
                <Label className="text-xs">{isAr ? "رابط استرداد كلمة المرور" : "Password setup link"}</Label>
                <div className="flex gap-2">
                  <Input readOnly value={result.recoveryLink} className="text-xs font-mono" />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(result.recoveryLink!);
                      setCopied(true);
                      toast.success(isAr ? "نُسخ" : "Copied");
                    }}
                  >
                    {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => handleClose(false)}>{isAr ? "تمّ" : "Done"}</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label>{isAr ? "الإيميل" : "Email"}</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <Label>{isAr ? "الاسم" : "Display name"}</Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </div>
            <div>
              <Label>{isAr ? "الهاتف (اختياري)" : "Phone (optional)"}</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div>
              <Label>{isAr ? "السبب" : "Reason"}</Label>
              <Textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={isAr ? "مثال: دعوة موظّف جديد" : "e.g. Inviting a new staff member"}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)} disabled={busy}>
                {isAr ? "إلغاء" : "Cancel"}
              </Button>
              <Button
                onClick={submit}
                disabled={busy || !email.trim() || !displayName.trim() || reason.trim().length < 3}
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin me-1" />}
                {isAr ? "إنشاء" : "Create"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────
// Recovery Link Dialog (stays open after generation to display link)
// ─────────────────────────────────────────────
function RecoveryLinkDialog({
  open, onOpenChange, result, copied, onCopy, onGenerate,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  result: { url: string; type: string } | null;
  copied: boolean;
  onCopy: () => void;
  onGenerate: (reason: string) => Promise<void>;
}) {
  const { locale } = useLocale();
  const isAr = locale === "ar";
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    if (!open) { setReason(""); setBusy(false); }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isAr ? "توليد رابط استرداد كلمة المرور" : "Generate password recovery link"}
          </DialogTitle>
          <DialogDescription>
            {isAr
              ? "ستحصل على رابط لإرساله للمستخدم عبر واتساب أو أيّ قناة. صالح لمدّة محدودة."
              : "You'll get a one-time link to share with the user via WhatsApp or any channel."}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <div className="space-y-2 rounded-none border border-border bg-muted/30 p-3">
              <Label className="text-xs">
                {isAr ? "الرابط (انسخه وأرسله)" : "Link (copy & share)"}
              </Label>
              <div className="flex gap-2">
                <Input readOnly value={result.url} className="text-xs font-mono" />
                <Button type="button" size="sm" variant="outline" onClick={onCopy}>
                  {copied ? <CheckCircle2 className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)}>{isAr ? "تمّ" : "Done"}</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label>{isAr ? "السبب" : "Reason"}</Label>
              <Textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={isAr ? "مثال: الإيميل لم يصل" : "e.g. Verification email didn't arrive"}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                {isAr ? "إلغاء" : "Cancel"}
              </Button>
              <Button
                onClick={async () => {
                  if (reason.trim().length < 3) {
                    toast.error(isAr ? "السبب مطلوب (٣ أحرف على الأقلّ)." : "Reason required.");
                    return;
                  }
                  setBusy(true);
                  try { await onGenerate(reason.trim()); }
                  catch (e) { toast.error((e as Error).message); }
                  finally { setBusy(false); }
                }}
                disabled={busy || reason.trim().length < 3}
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin me-1" />}
                {isAr ? "توليد" : "Generate"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────
// Quick role grant button — visible only to super_admin on customers with no team role.
// Lets super admin grant staff/admin/super_admin without leaving the user page.
function GrantTeamRoleButton({
  userId,
  isAr,
  onDone,
}: {
  userId: string;
  isAr: boolean;
  onDone: () => void;
}) {
  const grantFn = useServerFn(grantUserRole);
  const [open, setOpen] = React.useState(false);
  const [role, setRole] = React.useState<"staff" | "admin" | "super_admin">("staff");
  const [reason, setReason] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error(isAr ? "السبب مطلوب (٣ أحرف على الأقلّ)." : "Reason required.");
      return;
    }
    setBusy(true);
    try {
      await grantFn({ data: { userId, role, reason: reason.trim() } });
      toast.success(isAr ? "تمّ منح الصلاحيّة" : "Role granted");
      setOpen(false);
      setReason("");
      setRole("staff");
      onDone();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : (isAr ? "فشل" : "Failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <ShieldCheck className="h-3.5 w-3.5 me-1.5" />
        {isAr ? "منح صلاحيّات فريق" : "Grant team role"}
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isAr ? "منح صلاحيّات فريق" : "Grant team role"}</DialogTitle>
          <DialogDescription>
            {isAr
              ? "اختر الدور والسبب. ستُسجَّل العمليّة في سجلّ التعديلات."
              : "Choose a role and reason. This action will be audit-logged."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">{isAr ? "الدور" : "Role"}</Label>
            <Select value={role} onValueChange={(v) => setRole(v as typeof role)}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">{isAr ? "السبب (إجباريّ)" : "Reason (required)"}</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder={isAr ? "مثال: ترقية موظّف جديد" : "e.g. Onboarding new staff"}
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
            {isAr ? "إلغاء" : "Cancel"}
          </Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3}>
            {busy && <Loader2 className="h-4 w-4 animate-spin me-1.5" />}
            {isAr ? "منح" : "Grant"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ────────────────────────────────────────────────────────────
// KPI strip + tabs الجديدة (الطلبات / العناوين / المفضّلة)
// ────────────────────────────────────────────────────────────

type Kpis = {
  totalOrders: number;
  paidOrders: number;
  cancelledOrders: number;
  totalSpent: number;
  aov: number;
  lastOrderAt: string | null;
};

function KpiStrip({
  isAr, kpis, loyalty, balances, currency, walletTxns = [], onJumpToTab,
}: {
  isAr: boolean;
  kpis?: Kpis;
  loyalty: { points_balance: number; lifetime_earned: number };
  balances: Array<{ currency: string; available_credit: number; pending_credit: number; paid_out: number }>;
  currency: string;
  walletTxns?: WalletTxnRow[];
  onJumpToTab?: (tab: string) => void;
}) {
  const [pendingOpen, setPendingOpen] = React.useState(false);
  const k = kpis ?? { totalOrders: 0, paidOrders: 0, cancelledOrders: 0, totalSpent: 0, aov: 0, lastOrderAt: null };
  const fmt = (n: number) => n.toLocaleString(isAr ? "ar" : "en", { maximumFractionDigits: 0 });
  const cancelRate = k.totalOrders > 0 ? Math.round((k.cancelledOrders / k.totalOrders) * 100) : 0;

  // محفظة السفير: نختار العملة ذات أعلى رصيد متاح للعرض الرئيسيّ، وإن وجدت عملات أخرى نُلمح إليها
  const sortedBals = [...balances].sort((a, b) => b.available_credit - a.available_credit);
  const primary = sortedBals[0];
  const totalPending = balances.reduce((s, b) => s + b.pending_credit, 0);
  const ambAvailLabel = primary ? `${fmt(primary.available_credit)} ${primary.currency}` : "—";
  const ambAvailHint = sortedBals.length > 1
    ? sortedBals.slice(1).map((b) => `${fmt(b.available_credit)} ${b.currency}`).join(" · ")
    : null;

  // الحركات المعلَّقة فقط (مصدر «قيد الانتظار»)
  const pendingTxns = walletTxns.filter((t) => t.balance_state === "pending");
  const pendingByCurrency = pendingTxns.reduce<Record<string, number>>((acc, t) => {
    const cur = t.currency || "AED";
    acc[cur] = (acc[cur] ?? 0) + Number(t.amount ?? 0);
    return acc;
  }, {});

  const jumpOrders = onJumpToTab ? () => onJumpToTab("orders") : undefined;
  const jumpWallet = onJumpToTab ? () => onJumpToTab("wallet") : undefined;

  const items: Array<{ label: string; value: string; icon: React.ReactNode; tone?: string; hint?: string | null; onClick?: () => void; ctaLabel?: string }> = [
    { label: isAr ? "إجمالي الطلبات" : "Total orders", value: fmt(k.totalOrders), icon: <ShoppingBag className="h-4 w-4" />, onClick: jumpOrders, ctaLabel: isAr ? "الطلبات" : "orders" },
    { label: isAr ? "إجمالي الإنفاق" : "Total spent", value: `${fmt(k.totalSpent)} ${currency}`, icon: <TrendingUp className="h-4 w-4" />, onClick: jumpOrders, ctaLabel: isAr ? "تفاصيل" : "details" },
    { label: isAr ? "متوسّط الطلب" : "AOV", value: `${fmt(k.aov)} ${currency}`, icon: <Coins className="h-4 w-4" />, onClick: jumpOrders, ctaLabel: isAr ? "تفاصيل" : "details" },
    { label: isAr ? "آخر طلب" : "Last order", value: k.lastOrderAt ? new Date(k.lastOrderAt).toLocaleDateString(isAr ? "ar" : "en") : "—", icon: <Calendar className="h-4 w-4" />, onClick: k.lastOrderAt ? jumpOrders : undefined, ctaLabel: isAr ? "افتح" : "open" },
    { label: isAr ? "نقاط الولاء" : "Loyalty points", value: fmt(loyalty.points_balance), icon: <Star className="h-4 w-4" />, onClick: jumpWallet, ctaLabel: isAr ? "الحركات" : "history" },
    { label: isAr ? "رصيد السفير المتاح" : "Ambassador available", value: ambAvailLabel, icon: <Coins className="h-4 w-4" />, hint: ambAvailHint, onClick: jumpWallet, ctaLabel: isAr ? "الحركات" : "history" },
    {
      label: isAr ? "قيد الانتظار (السفير)" : "Ambassador pending",
      value: primary ? `${fmt(totalPending)} ${primary.currency}` : "—",
      icon: <Calendar className="h-4 w-4" />,
      tone: totalPending > 0 ? "text-amber-600" : "",
      onClick: totalPending > 0 || pendingTxns.length > 0 ? () => setPendingOpen(true) : undefined,
      ctaLabel: isAr ? "تفاصيل" : "details",
    },
    { label: isAr ? "نسبة الإلغاء" : "Cancel rate", value: `${cancelRate}%`, icon: <XCircle className="h-4 w-4" />, tone: cancelRate > 20 ? "text-amber-600" : "", onClick: k.cancelledOrders > 0 ? jumpOrders : undefined, ctaLabel: isAr ? "الملغاة" : "cancelled" },
  ];

  const fmtDate = (s: string) => new Date(s).toLocaleString(isAr ? "ar" : "en", {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
  const txnTypeLabelLocal = (t: string) => {
    const map: Record<string, { ar: string; en: string }> = {
      purchase_reward: { ar: "مكافأة شراء", en: "Purchase reward" },
      recruit_reward: { ar: "مكافأة تجنيد", en: "Recruit reward" },
      milestone_reward: { ar: "مكافأة Milestone", en: "Milestone reward" },
      coupon_hold: { ar: "حجز كوبون", en: "Coupon hold" },
      coupon_debit: { ar: "استبدال كوبون", en: "Coupon debit" },
      coupon_release: { ar: "إفراج كوبون", en: "Coupon release" },
      cash_payout: { ar: "سحب نقديّ", en: "Cash payout" },
      admin_adjustment: { ar: "تعديل أدمن", en: "Admin adjustment" },
      refund_reverse: { ar: "عكس بسبب استرداد", en: "Refund reverse" },
    };
    return map[t]?.[isAr ? "ar" : "en"] ?? t;
  };

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-8 gap-2">
        {items.map((it) => {
          const clickable = !!it.onClick;
          return (
            <Card
              key={it.label}
              onClick={it.onClick}
              className={`p-3 ${clickable ? "cursor-pointer hover:bg-muted/40 hover:border-amber-500/40 transition" : ""}`}
              role={clickable ? "button" : undefined}
              tabIndex={clickable ? 0 : undefined}
              onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); it.onClick?.(); } } : undefined}
            >
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                {it.icon}
                <span>{it.label}</span>
                {clickable && <span className="ms-auto text-[9px] text-amber-600">{it.ctaLabel ?? (isAr ? "تفاصيل" : "details")}</span>}
              </div>
              <div className={`text-base font-semibold mt-1 tabular-nums ${it.tone ?? ""}`}>{it.value}</div>
              {it.hint && <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">{it.hint}</div>}
            </Card>
          );
        })}
      </div>

      <Dialog open={pendingOpen} onOpenChange={setPendingOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isAr ? "تفاصيل الرصيد قيد الانتظار" : "Pending balance details"}</DialogTitle>
            <DialogDescription>
              {isAr
                ? "مبالغ مكتسبة من طلبات لم تكتمل فترة الضمان أو تنتظر صرف كوبون. تتحوّل تلقائيّاً إلى «متاح» بعد انقضاء فترة الانتظار وعدم وجود استرداد."
                : "Amounts earned from orders still in the refund window or awaiting coupon settlement. They roll to 'available' once the holding period passes with no refund."}
            </DialogDescription>
          </DialogHeader>

          {/* مجاميع لكل عملة */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(pendingByCurrency).length === 0 ? (
              <Badge variant="outline">{isAr ? "لا حركات معلَّقة" : "No pending movements"}</Badge>
            ) : (
              Object.entries(pendingByCurrency).map(([cur, sum]) => (
                <Badge key={cur} variant="outline" className="text-amber-700 border-amber-500/40 bg-amber-500/5">
                  {fmt(sum)} {cur}
                </Badge>
              ))
            )}
          </div>

          {pendingTxns.length > 0 && (
            <div className="overflow-x-auto border rounded-none">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-xs text-muted-foreground">
                  <tr>
                    <th className="text-start px-3 py-2">{isAr ? "التاريخ" : "Date"}</th>
                    <th className="text-start px-3 py-2">{isAr ? "النوع" : "Type"}</th>
                    <th className="text-end px-3 py-2">{isAr ? "المبلغ" : "Amount"}</th>
                    <th className="text-start px-3 py-2">{isAr ? "كوبون / طلب" : "Coupon / Order"}</th>
                    <th className="text-start px-3 py-2">{isAr ? "ملاحظة" : "Note"}</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingTxns.map((t) => {
                    const amount = Number(t.amount ?? 0);
                    return (
                      <tr key={t.id} className="border-t border-border/40">
                        <td className="px-3 py-2 text-xs text-muted-foreground tabular-nums whitespace-nowrap">{fmtDate(t.created_at)}</td>
                        <td className="px-3 py-2 text-xs">{txnTypeLabelLocal(t.txn_type)}</td>
                        <td className={`px-3 py-2 text-end font-semibold tabular-nums ${amount >= 0 ? "text-emerald-600" : "text-destructive"}`}>
                          {amount > 0 ? "+" : ""}{amount.toFixed(2)} {t.currency}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono">
                          {t.related_coupon_code ?? t.related_order_id ?? "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[260px]">{t.note ?? "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

type OrderLineItem = {
  title?: string;
  name?: string;
  quantity?: number;
  price?: string | number;
  variant_title?: string | null;
  sku?: string | null;
  product_id?: number | string;
};

type ShopifyAddress = {
  name?: string;
  address1?: string;
  address2?: string;
  city?: string;
  province?: string;
  country?: string;
  zip?: string;
  phone?: string;
};

type OrderRow = {
  id: string;
  shopify_order_id?: string | null;
  order_name: string | null;
  total_amount: number | string | null;
  currency: string | null;
  financial_status: string | null;
  fulfillment_status: string | null;
  is_gift: boolean | null;
  shopify_created_at: string | null;
  shipping_city: string | null;
  shipping_country: string | null;
  shipping_address1?: string | null;
  shipping_address2?: string | null;
  market?: string | null;
  discount_code?: string | null;
  line_items_count?: number | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  raw_payload?: unknown;
};

// Labels مشتركة لحركات الولاء/المحفظة
const LOYALTY_REASON_LABELS: Record<string, { ar: string; en: string }> = {
  signup_bonus: { ar: "علاوة تسجيل", en: "Signup bonus" },
  order_earn: { ar: "كسب من طلب", en: "Order earn" },
  redemption: { ar: "استبدال", en: "Redemption" },
  refund_reverse: { ar: "عكس بسبب استرداد", en: "Refund reverse" },
  admin_adjustment: { ar: "تعديل أدمن", en: "Admin adjustment" },
};
const WALLET_TXN_LABELS: Record<string, { ar: string; en: string }> = {
  purchase_reward: { ar: "مكافأة شراء", en: "Purchase reward" },
  purchase_credit: { ar: "مكافأة شراء", en: "Purchase credit" },
  recruit_reward: { ar: "مكافأة تجنيد", en: "Recruit reward" },
  milestone_reward: { ar: "مكافأة Milestone", en: "Milestone reward" },
  coupon_hold: { ar: "حجز كوبون", en: "Coupon hold" },
  coupon_debit: { ar: "استبدال كوبون", en: "Coupon debit" },
  coupon_release: { ar: "إفراج كوبون", en: "Coupon release" },
  cash_payout: { ar: "سحب نقديّ", en: "Cash payout" },
  admin_adjustment: { ar: "تعديل أدمن", en: "Admin adjustment" },
  refund_reverse: { ar: "عكس بسبب استرداد", en: "Refund reverse" },
};
function loyaltyReasonLabel(r: string | null, isAr: boolean): string {
  if (!r) return "—";
  return LOYALTY_REASON_LABELS[r]?.[isAr ? "ar" : "en"] ?? r;
}
function walletTxnLabel(t: string, isAr: boolean): string {
  return WALLET_TXN_LABELS[t]?.[isAr ? "ar" : "en"] ?? t;
}

type OrderPayload = {
  line_items?: OrderLineItem[];
  shipping_address?: ShopifyAddress;
  billing_address?: ShopifyAddress;
  note?: string | null;
  note_attributes?: Array<{ name: string; value: string }>;
  subtotal_price?: string;
  total_discounts?: string;
  total_shipping_price_set?: { shop_money?: { amount?: string } };
  total_tax?: string;
};

function statusTone(s: string | null): string {
  const x = (s ?? "").toLowerCase();
  if (x === "paid") return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30";
  if (x === "partially_paid" || x === "authorized") return "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30";
  if (x === "refunded" || x === "voided" || x === "partially_refunded") return "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30";
  if (x === "pending") return "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30";
  return "bg-muted text-muted-foreground border-border";
}

function formatAddress(o: OrderRow): string {
  const payload = (o.raw_payload ?? {}) as OrderPayload;
  const sa = payload.shipping_address;
  const parts = [
    sa?.name,
    sa?.address1 ?? o.shipping_address1,
    sa?.address2 ?? o.shipping_address2,
    sa?.city ?? o.shipping_city,
    sa?.province,
    sa?.zip,
    sa?.country ?? o.shipping_country,
  ].filter(Boolean);
  return parts.join(" · ");
}

function OrderDetailCard({
  isAr, o, highlight = false, loyaltyTxns = [], walletTxns = [],
}: {
  isAr: boolean;
  o: OrderRow;
  highlight?: boolean;
  loyaltyTxns?: LoyaltyTxnRow[];
  walletTxns?: WalletTxnRow[];
}) {
  const payload = (o.raw_payload ?? {}) as OrderPayload;
  const items = payload.line_items ?? [];
  const sa = payload.shipping_address;
  const phone = sa?.phone ?? o.customer_phone ?? null;
  const subtotal = payload.subtotal_price ? Number(payload.subtotal_price) : null;
  const discount = payload.total_discounts ? Number(payload.total_discounts) : null;
  const shipping = payload.total_shipping_price_set?.shop_money?.amount
    ? Number(payload.total_shipping_price_set.shop_money.amount) : null;
  const tax = payload.total_tax ? Number(payload.total_tax) : null;
  const giftMsg = payload.note_attributes?.find((a) => /gift|message|بطاقة|إهداء/i.test(a.name))?.value
    ?? (o.is_gift ? payload.note ?? null : null);

  return (
    <Card className={`p-4 space-y-3 ${highlight ? "border-primary/50 bg-primary/5" : ""}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {highlight && (
            <Badge variant="default" className="text-[10px]">
              {isAr ? "آخر طلب" : "Latest order"}
            </Badge>
          )}
          <span className="font-semibold">{o.order_name ?? o.id.slice(0, 8)}</span>
          {o.is_gift && <Badge variant="outline" className="text-[10px]">🎁 {isAr ? "هديّة" : "Gift"}</Badge>}
          {o.market && <Badge variant="outline" className="text-[10px]">{o.market}</Badge>}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {o.shopify_created_at && (
            <span>{new Date(o.shopify_created_at).toLocaleString(isAr ? "ar" : "en")}</span>
          )}
          <Badge variant="outline" className={`text-[10px] ${statusTone(o.financial_status)}`}>
            {o.financial_status ?? "—"}
          </Badge>
          {o.fulfillment_status && (
            <Badge variant="outline" className="text-[10px]">{o.fulfillment_status}</Badge>
          )}
        </div>
      </div>

      {/* Line items */}
      {items.length > 0 ? (
        <div className="rounded-none border border-border/50 divide-y divide-border/50">
          {items.map((it, i) => (
            <div key={i} className="p-2 flex items-center justify-between gap-2 text-sm">
              <div className="min-w-0">
                <div className="font-medium truncate">{it.title ?? it.name ?? "—"}</div>
                {it.variant_title && (
                  <div className="text-[11px] text-muted-foreground">{it.variant_title}</div>
                )}
                {it.sku && <div className="text-[10px] text-muted-foreground">SKU: {it.sku}</div>}
              </div>
              <div className="text-end shrink-0">
                <div className="text-xs text-muted-foreground">x{it.quantity ?? 1}</div>
                {it.price && (
                  <div className="tabular-nums text-sm">
                    {Number(it.price).toFixed(2)} {o.currency}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">
          {isAr ? `عدد المنتجات: ${o.line_items_count ?? 0} (تفاصيل غير محفوظة)` : `Items: ${o.line_items_count ?? 0} (no detail saved)`}
        </div>
      )}

      {/* Totals */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        {subtotal !== null && (
          <div><span className="text-muted-foreground">{isAr ? "المجموع الفرعيّ" : "Subtotal"}: </span>
            <span className="tabular-nums">{subtotal.toFixed(2)}</span></div>
        )}
        {discount !== null && discount > 0 && (
          <div><span className="text-muted-foreground">{isAr ? "الخصم" : "Discount"}: </span>
            <span className="tabular-nums text-rose-600">−{discount.toFixed(2)}</span>
            {o.discount_code && <span className="ms-1 text-[10px] text-muted-foreground">({o.discount_code})</span>}
          </div>
        )}
        {shipping !== null && (
          <div><span className="text-muted-foreground">{isAr ? "الشحن" : "Shipping"}: </span>
            <span className="tabular-nums">{shipping.toFixed(2)}</span></div>
        )}
        {tax !== null && tax > 0 && (
          <div><span className="text-muted-foreground">{isAr ? "ضريبة" : "Tax"}: </span>
            <span className="tabular-nums">{tax.toFixed(2)}</span></div>
        )}
        <div className="col-span-2 sm:col-span-4 pt-1 border-t border-border/40">
          <span className="text-muted-foreground">{isAr ? "الإجماليّ" : "Total"}: </span>
          <span className="tabular-nums font-semibold">{Number(o.total_amount ?? 0).toFixed(2)} {o.currency}</span>
        </div>
      </div>

      {/* Shipping address */}
      <div className="text-xs">
        <div className="flex items-center gap-1 text-muted-foreground mb-1">
          <MapPin className="h-3 w-3" />
          {isAr ? (o.is_gift ? "عنوان المُهدى إليه" : "عنوان الشحن") : (o.is_gift ? "Recipient address" : "Shipping address")}
        </div>
        <div>{formatAddress(o) || (isAr ? "— لا يوجد" : "— none")}</div>
        {phone && <div className="text-muted-foreground tabular-nums" dir="ltr">{phone}</div>}
      </div>

      {/* Gift message */}
      {giftMsg && (
        <div className="text-xs rounded bg-muted/40 p-2">
          <span className="text-muted-foreground">{isAr ? "بطاقة الإهداء" : "Gift card"}: </span>
          <span className="italic">{giftMsg}</span>
        </div>
      )}

      {/* حركات النقاط ومحفظة السفير المرتبطة بهذا الطلب */}
      {(loyaltyTxns.length > 0 || walletTxns.length > 0) && (
        <div className="text-xs rounded border border-border/50 bg-muted/20 p-2.5 space-y-2">
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Coins className="h-3.5 w-3.5" />
            <span className="font-medium">
              {isAr ? "حركات النقاط والمحفظة بسبب هذا الطلب" : "Loyalty / wallet movements for this order"}
            </span>
          </div>

          {loyaltyTxns.length > 0 && (
            <ul className="space-y-1">
              {loyaltyTxns.map((t) => {
                const pos = t.points > 0;
                return (
                  <li key={t.id} className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div>
                        <Badge variant="outline" className="text-[10px] me-1">
                          <Star className="h-2.5 w-2.5 me-0.5 text-amber-600" />
                          {isAr ? "ولاء" : "Loyalty"}
                        </Badge>
                        <span className="text-muted-foreground">{loyaltyReasonLabel(t.reason, isAr)}</span>
                      </div>
                      {t.note && <div className="text-muted-foreground text-[10px] italic mt-0.5">{t.note}</div>}
                    </div>
                    <div className={`tabular-nums whitespace-nowrap font-medium ${pos ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"}`}>
                      {pos ? "+" : ""}{t.points} {isAr ? "نقطة" : "pts"}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {walletTxns.length > 0 && (
            <ul className="space-y-1">
              {walletTxns.map((t) => {
                const amt = Number(t.amount);
                const pos = amt > 0;
                return (
                  <li key={t.id} className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div>
                        <Badge variant="outline" className="text-[10px] me-1">
                          <Wallet className="h-2.5 w-2.5 me-0.5 text-primary" />
                          {isAr ? "محفظة" : "Wallet"}
                        </Badge>
                        <span className="text-muted-foreground">{walletTxnLabel(t.txn_type, isAr)}</span>
                        {t.balance_state && (
                          <Badge variant="outline" className="ms-1 text-[9px]">{t.balance_state}</Badge>
                        )}
                        {t.related_coupon_code && (
                          <span className="ms-1 text-[10px] text-muted-foreground">({t.related_coupon_code})</span>
                        )}
                      </div>
                      {t.note && <div className="text-muted-foreground text-[10px] italic mt-0.5">{t.note}</div>}
                    </div>
                    <div className={`tabular-nums whitespace-nowrap font-medium ${pos ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"}`}>
                      {pos ? "+" : ""}{amt.toFixed(2)} {t.currency}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}


      <div className="flex justify-end">
        <Button asChild size="sm" variant="ghost">
          <Link to="/admin/orders" search={{ q: o.order_name ?? o.id } as never}>
            <ExternalLink className="h-3.5 w-3.5 me-1" />
            {isAr ? "صفحة الطلب" : "Open order"}
          </Link>
        </Button>
      </div>
    </Card>
  );
}

function OrdersTab({
  isAr, locale, orders, loyaltyTxns, walletTxns,
}: {
  isAr: boolean;
  locale: "en" | "ar" | "fr";
  orders: OrderRow[];
  loyaltyTxns: LoyaltyTxnRow[];
  walletTxns: WalletTxnRow[];
}) {
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const toggle = (id: string) => {
    setExpanded((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  // فهرس حركات الولاء/المحفظة حسب related_order_id لربط سريع
  const loyaltyByOrder = React.useMemo(() => {
    const m = new Map<string, LoyaltyTxnRow[]>();
    for (const t of loyaltyTxns) {
      const k = (t.related_order_id ?? "").toString();
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(t);
    }
    return m;
  }, [loyaltyTxns]);
  const walletByOrder = React.useMemo(() => {
    const m = new Map<string, WalletTxnRow[]>();
    for (const t of walletTxns) {
      const k = (t.related_order_id ?? "").toString();
      if (!k) continue;
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(t);
    }
    return m;
  }, [walletTxns]);

  const orderKeys = (o: OrderRow) =>
    [o.shopify_order_id, o.order_name, o.id].filter(Boolean).map((s) => String(s));
  const txnsFor = (
    o: OrderRow,
    map: Map<string, LoyaltyTxnRow[]> | Map<string, WalletTxnRow[]>,
  ) => {
    const out: (LoyaltyTxnRow | WalletTxnRow)[] = [];
    const seen = new Set<string>();
    for (const k of orderKeys(o)) {
      const arr = map.get(k);
      if (!arr) continue;
      for (const t of arr) {
        if (seen.has(t.id)) continue;
        seen.add(t.id);
        out.push(t);
      }
    }
    return out;
  };

  if (orders.length === 0) {
    return (
      <Card className="p-6 text-center">
        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
          <Gift className="h-5 w-5 text-muted-foreground" />
        </div>
        <h4 className="text-sm font-medium">{t(locale, "admin.users.orders.empty.title")}</h4>
        <p className="mt-1 text-xs text-muted-foreground max-w-xs mx-auto">
          {t(locale, "admin.users.orders.empty.body")}
        </p>
        <Button asChild size="sm" variant="outline" className="mt-3">
          <Link to="/admin/gifts-hub">
            {t(locale, "admin.users.orders.empty.cta")}
          </Link>
        </Button>
      </Card>
    );
  }

  // Breakdown
  const giftCount = orders.filter((o) => o.is_gift).length;
  const selfCount = orders.length - giftCount;
  const statusMap: Record<string, number> = {};
  const marketMap: Record<string, number> = {};
  for (const o of orders) {
    const s = (o.financial_status ?? "—").toLowerCase();
    statusMap[s] = (statusMap[s] ?? 0) + 1;
    const m = o.market ?? "—";
    marketMap[m] = (marketMap[m] ?? 0) + 1;
  }
  const last = orders[0];
  const rest = orders.slice(1);

  return (
    <div className="space-y-4">
      {/* Breakdown strip */}
      <Card className="p-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-[11px] text-muted-foreground">{isAr ? "نوع الطلبات" : "Order types"}</div>
            <div className="flex gap-2 mt-0.5 flex-wrap">
              <Badge variant="outline" className="text-xs">🎁 {isAr ? "هديّة" : "Gift"}: {giftCount}</Badge>
              <Badge variant="outline" className="text-xs">👤 {isAr ? "لنفسي" : "Self"}: {selfCount}</Badge>
            </div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground">{isAr ? "حسب الحالة" : "By status"}</div>
            <div className="flex gap-1.5 mt-0.5 flex-wrap">
              {Object.entries(statusMap).map(([k, v]) => (
                <Badge key={k} variant="outline" className={`text-[10px] ${statusTone(k)}`}>{k}: {v}</Badge>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground">{isAr ? "حسب السوق" : "By market"}</div>
            <div className="flex gap-1.5 mt-0.5 flex-wrap">
              {Object.entries(marketMap).map(([k, v]) => (
                <Badge key={k} variant="outline" className="text-[10px]">{k}: {v}</Badge>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] text-muted-foreground">{isAr ? "الإجماليّ" : "Count"}</div>
            <div className="text-lg font-semibold tabular-nums">{orders.length}</div>
          </div>
        </div>
      </Card>

      {/* Latest order full detail */}
      <OrderDetailCard
        isAr={isAr}
        o={last}
        highlight
        loyaltyTxns={txnsFor(last, loyaltyByOrder) as LoyaltyTxnRow[]}
        walletTxns={txnsFor(last, walletByOrder) as WalletTxnRow[]}
      />

      {/* Previous orders — collapsible */}
      {rest.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="px-3 py-2 text-xs text-muted-foreground bg-muted/30 border-b border-border/40">
            {isAr ? `الطلبات السابقة (${rest.length})` : `Previous orders (${rest.length})`}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/20 text-xs text-muted-foreground">
                <tr>
                  <th className="text-start p-2.5 font-medium">{isAr ? "الطلب" : "Order"}</th>
                  <th className="text-start p-2.5 font-medium">{isAr ? "النوع" : "Type"}</th>
                  <th className="text-start p-2.5 font-medium">{isAr ? "التاريخ" : "Date"}</th>
                  <th className="text-start p-2.5 font-medium">{isAr ? "الحالة" : "Status"}</th>
                  <th className="text-start p-2.5 font-medium">{isAr ? "الوجهة" : "Ship to"}</th>
                  <th className="text-end p-2.5 font-medium">{isAr ? "المبلغ" : "Amount"}</th>
                  <th className="p-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {rest.map((o) => {
                  const isOpen = expanded.has(o.id);
                  return (
                    <React.Fragment key={o.id}>
                      <tr className="border-t border-border/40 hover:bg-muted/30">
                        <td className="p-2.5 font-medium">{o.order_name ?? o.id.slice(0, 8)}</td>
                        <td className="p-2.5">
                          {o.is_gift ? <Badge variant="outline" className="text-[10px]">🎁 {isAr ? "هديّة" : "Gift"}</Badge>
                            : <Badge variant="outline" className="text-[10px]">👤 {isAr ? "لنفسي" : "Self"}</Badge>}
                        </td>
                        <td className="p-2.5 text-muted-foreground text-xs">
                          {o.shopify_created_at ? new Date(o.shopify_created_at).toLocaleDateString(isAr ? "ar" : "en") : "—"}
                        </td>
                        <td className="p-2.5">
                          <Badge variant="outline" className={`text-[10px] ${statusTone(o.financial_status)}`}>
                            {o.financial_status ?? "—"}
                          </Badge>
                        </td>
                        <td className="p-2.5 text-xs text-muted-foreground">
                          {[o.shipping_city, o.shipping_country].filter(Boolean).join(", ") || "—"}
                        </td>
                        <td className="p-2.5 text-end tabular-nums">
                          {Number(o.total_amount ?? 0).toFixed(2)} {o.currency ?? ""}
                        </td>
                        <td className="p-2.5">
                          <Button size="sm" variant="ghost" onClick={() => toggle(o.id)}>
                            {isOpen ? (isAr ? "إخفاء" : "Hide") : (isAr ? "تفاصيل" : "Detail")}
                          </Button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-border/40 bg-muted/10">
                          <td colSpan={7} className="p-3">
                            <OrderDetailCard
                              isAr={isAr}
                              o={o}
                              loyaltyTxns={txnsFor(o, loyaltyByOrder) as LoyaltyTxnRow[]}
                              walletTxns={txnsFor(o, walletByOrder) as WalletTxnRow[]}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

type RecipientRow = {
  id: string;
  recipient_name: string | null;
  recipient_phone: string;
  country: string | null;
  city: string | null;
  address1: string | null;
  driver_notes: string | null;
  last_used_at: string;
};

function AddressesTab({
  isAr, locale, recipients: initialRecipients, profile: initialProfile, userId,
}: {
  isAr: boolean;
  locale: "en" | "ar" | "fr";
  recipients: RecipientRow[];
  profile: { self_country?: string | null; self_city?: string | null; self_address1?: string | null; self_driver_notes?: string | null } | null;
  userId: string;
}) {
  const [recipients, setRecipients] = React.useState<RecipientRow[]>(initialRecipients);
  const [profile, setProfile] = React.useState(initialProfile);
  React.useEffect(() => { setRecipients(initialRecipients); }, [initialRecipients]);
  React.useEffect(() => { setProfile(initialProfile); }, [initialProfile]);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [form, setForm] = React.useState({
    recipient_name: "",
    recipient_phone: "",
    country: "",
    city: "",
    address1: "",
    driver_notes: "",
  });
  const createFn = useServerFn(createTrialRecipientAdmin);
  const qc = useQueryClient();

  const hasSelf = !!(profile?.self_country || profile?.self_city || profile?.self_address1);

  const resetForm = () => setForm({
    recipient_name: "",
    recipient_phone: "",
    country: "",
    city: "",
    address1: "",
    driver_notes: "",
  });

  const handleSave = async () => {
    try {
      const created = await createFn({
        data: {
          userId,
          recipient_name: form.recipient_name,
          recipient_phone: form.recipient_phone,
          country: form.country,
          city: form.city,
          address1: form.address1,
          driver_notes: form.driver_notes,
        },
      });
      toast.success(t(locale, "admin.users.addresses.dialog.success"));
      setRecipients((prev) => [created as unknown as RecipientRow, ...prev]);
      qc.invalidateQueries({ queryKey: ["user-details-admin", userId] });
      resetForm();
      setDialogOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? t(locale, "admin.users.addresses.dialog.error"));
    }
  };

  const canSave =
    form.recipient_name.trim() &&
    form.recipient_phone.trim() &&
    form.country.trim() &&
    form.city.trim() &&
    form.address1.trim();

  if (!hasSelf && recipients.length === 0) {
    return (
      <>
        <Card className="p-6 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-muted">
            <MapPin className="h-5 w-5 text-muted-foreground" />
          </div>
          <h4 className="text-sm font-medium">{t(locale, "admin.users.addresses.empty.title")}</h4>
          <p className="mt-1 text-xs text-muted-foreground max-w-xs mx-auto">
            {t(locale, "admin.users.addresses.empty.body")}
          </p>
          <Button size="sm" variant="outline" className="mt-3" onClick={() => setDialogOpen(true)}>
            {t(locale, "admin.users.addresses.empty.cta")}
          </Button>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t(locale, "admin.users.addresses.dialog.title")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label htmlFor="r-name">{t(locale, "admin.users.addresses.dialog.name")}</Label>
                <Input id="r-name" value={form.recipient_name} onChange={(e) => setForm((f) => ({ ...f, recipient_name: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="r-phone">{t(locale, "admin.users.addresses.dialog.phone")}</Label>
                <Input id="r-phone" value={form.recipient_phone} onChange={(e) => setForm((f) => ({ ...f, recipient_phone: e.target.value }))} dir="ltr" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="r-country">{t(locale, "admin.users.addresses.dialog.country")}</Label>
                  <Input id="r-country" value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="r-city">{t(locale, "admin.users.addresses.dialog.city")}</Label>
                  <Input id="r-city" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="r-address">{t(locale, "admin.users.addresses.dialog.address")}</Label>
                <Textarea id="r-address" value={form.address1} onChange={(e) => setForm((f) => ({ ...f, address1: e.target.value }))} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="r-notes">{t(locale, "admin.users.addresses.dialog.notes")}</Label>
                <Input id="r-notes" value={form.driver_notes} onChange={(e) => setForm((f) => ({ ...f, driver_notes: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { resetForm(); setDialogOpen(false); }}>
                {t(locale, "admin.users.addresses.dialog.cancel")}
              </Button>
              <Button onClick={handleSave} disabled={!canSave}>
                {t(locale, "admin.users.addresses.dialog.save")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }
  return (
    <div className="space-y-3">
      {hasSelf && (
        <Card className="p-4">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
            <MapPin className="h-3.5 w-3.5" />
            {isAr ? "عنوان العميل (هديّة لنفسي)" : "Self address"}
          </div>
          <div className="text-sm space-y-0.5">
            <div>{[profile?.self_country, profile?.self_city].filter(Boolean).join(" · ")}</div>
            {profile?.self_address1 && <div className="text-muted-foreground">{profile.self_address1}</div>}
            {profile?.self_driver_notes && (
              <div className="text-xs text-muted-foreground italic">📝 {profile.self_driver_notes}</div>
            )}
          </div>
        </Card>
      )}
      {recipients.map((r) => (
        <Card key={r.id} className="p-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="text-sm space-y-0.5">
              <div className="font-semibold">{r.recipient_name ?? (isAr ? "بلا اسم" : "Unnamed")}</div>
              <div className="text-muted-foreground tabular-nums" dir="ltr">{r.recipient_phone}</div>
              <div>{[r.country, r.city].filter(Boolean).join(" · ") || "—"}</div>
              {r.address1 && <div className="text-muted-foreground">{r.address1}</div>}
              {r.driver_notes && <div className="text-xs text-muted-foreground italic">📝 {r.driver_notes}</div>}
            </div>
            <div className="text-[10px] text-muted-foreground">
              {isAr ? "آخر استخدام" : "Last used"}: {new Date(r.last_used_at).toLocaleDateString(isAr ? "ar" : "en")}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

type WishlistRow = {
  id: string;
  product_id: string;
  product_handle: string;
  product_title: string | null;
  product_image: string | null;
  product_price: number | string | null;
  product_currency: string | null;
  created_at: string;
};

function FavoritesTab({ isAr, wishlist }: { isAr: boolean; wishlist: WishlistRow[] }) {
  if (wishlist.length === 0) {
    return (
      <Card className="p-6 text-center text-sm text-muted-foreground">
        {isAr ? "لا توجد منتجات في المفضّلة." : "No wishlist items."}
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      <div className="text-xs text-muted-foreground px-1">
        {isAr
          ? "منتجات أبدى العميل اهتماماً بها — مرشّحة لحملة تذكير."
          : "Products the customer favorited — ideal for a reminder campaign."}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {wishlist.map((w) => (
          <Card key={w.id} className="p-3 flex items-center gap-3">
            {w.product_image ? (
              <img src={w.product_image} alt="" className="h-12 w-12 rounded object-cover bg-muted shrink-0" loading="lazy" />
            ) : (
              <div className="h-12 w-12 rounded bg-muted shrink-0" />
            )}
            <div className="flex-1 min-w-0 text-sm">
              <div className="font-medium truncate">{w.product_title ?? w.product_handle}</div>
              <div className="text-xs text-muted-foreground tabular-nums">
                {w.product_price ? `${Number(w.product_price).toFixed(2)} ${w.product_currency ?? ""}` : ""}
                {" · "}
                {new Date(w.created_at).toLocaleDateString(isAr ? "ar" : "en")}
              </div>
            </div>
            <Button asChild size="sm" variant="ghost">
              <a href={`/product/${w.product_handle}`} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────
// تاب «المحفظة» — سجلّ حركات نقاط الولاء + محفظة السفير
// ────────────────────────────────────────────────────────────

type LoyaltyTxnRow = {
  id: string;
  points: number;
  reason: string | null;
  note: string | null;
  related_order_id: string | null;
  created_at: string;
};

type WalletTxnRow = {
  id: string;
  txn_type: string;
  amount: number | string;
  currency: string;
  balance_state: string | null;
  related_coupon_code: string | null;
  related_payout_id: string | null;
  related_order_id?: string | null;
  note: string | null;
  created_at: string;
};

function WalletHistoryTab({
  isAr,
  loyaltyTxns,
  walletTxns,
}: {
  isAr: boolean;
  loyaltyTxns: LoyaltyTxnRow[];
  walletTxns: WalletTxnRow[];
}) {
  const fmtDate = (s: string) => new Date(s).toLocaleString(isAr ? "ar" : "en", {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });

  const reasonLabel = (r: string | null) => {
    if (!r) return "—";
    const map: Record<string, { ar: string; en: string }> = {
      signup_bonus: { ar: "علاوة تسجيل", en: "Signup bonus" },
      order_earn: { ar: "كسب من طلب", en: "Order earn" },
      redemption: { ar: "استبدال", en: "Redemption" },
      refund_reverse: { ar: "عكس بسبب استرداد", en: "Refund reverse" },
      admin_adjustment: { ar: "تعديل أدمن", en: "Admin adjustment" },
    };
    return map[r]?.[isAr ? "ar" : "en"] ?? r;
  };

  const txnTypeLabel = (t: string) => {
    const map: Record<string, { ar: string; en: string }> = {
      purchase_reward: { ar: "مكافأة شراء", en: "Purchase reward" },
      recruit_reward: { ar: "مكافأة تجنيد", en: "Recruit reward" },
      milestone_reward: { ar: "مكافأة Milestone", en: "Milestone reward" },
      coupon_hold: { ar: "حجز كوبون", en: "Coupon hold" },
      coupon_debit: { ar: "استبدال كوبون", en: "Coupon debit" },
      coupon_release: { ar: "إفراج كوبون", en: "Coupon release" },
      cash_payout: { ar: "سحب نقديّ", en: "Cash payout" },
      admin_adjustment: { ar: "تعديل أدمن", en: "Admin adjustment" },
      refund_reverse: { ar: "عكس بسبب استرداد", en: "Refund reverse" },
    };
    return map[t]?.[isAr ? "ar" : "en"] ?? t;
  };

  return (
    <div className="space-y-4">
      {/* نقاط الولاء */}
      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/30 flex items-center gap-2">
          <Star className="h-4 w-4 text-amber-600" />
          <h3 className="font-semibold text-sm">{isAr ? "حركات نقاط الولاء" : "Loyalty point movements"}</h3>
          <span className="text-xs text-muted-foreground ms-auto">({loyaltyTxns.length})</span>
        </div>
        {loyaltyTxns.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            {isAr ? "لا حركات نقاط بعد." : "No loyalty transactions yet."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/20 text-xs text-muted-foreground">
                <tr>
                  <th className="text-start px-3 py-2">{isAr ? "التاريخ" : "Date"}</th>
                  <th className="text-start px-3 py-2">{isAr ? "السبب" : "Reason"}</th>
                  <th className="text-end px-3 py-2">{isAr ? "النقاط" : "Points"}</th>
                  <th className="text-start px-3 py-2">{isAr ? "ملاحظة" : "Note"}</th>
                </tr>
              </thead>
              <tbody>
                {loyaltyTxns.map((t) => (
                  <tr key={t.id} className="border-t border-border/40">
                    <td className="px-3 py-2 text-xs text-muted-foreground tabular-nums">{fmtDate(t.created_at)}</td>
                    <td className="px-3 py-2 text-xs">{reasonLabel(t.reason)}</td>
                    <td className={`px-3 py-2 text-end font-semibold tabular-nums ${t.points >= 0 ? "text-emerald-600" : "text-destructive"}`}>
                      {t.points > 0 ? "+" : ""}{t.points}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[300px]">{t.note ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* محفظة السفير */}
      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b bg-muted/30 flex items-center gap-2">
          <Coins className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-sm">{isAr ? "حركات محفظة السفير" : "Ambassador wallet movements"}</h3>
          <span className="text-xs text-muted-foreground ms-auto">({walletTxns.length})</span>
        </div>
        {walletTxns.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            {isAr ? "لا حركات محفظة بعد." : "No wallet transactions yet."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/20 text-xs text-muted-foreground">
                <tr>
                  <th className="text-start px-3 py-2">{isAr ? "التاريخ" : "Date"}</th>
                  <th className="text-start px-3 py-2">{isAr ? "النوع" : "Type"}</th>
                  <th className="text-start px-3 py-2">{isAr ? "الحالة" : "State"}</th>
                  <th className="text-end px-3 py-2">{isAr ? "المبلغ" : "Amount"}</th>
                  <th className="text-start px-3 py-2">{isAr ? "كوبون" : "Coupon"}</th>
                  <th className="text-start px-3 py-2">{isAr ? "ملاحظة" : "Note"}</th>
                </tr>
              </thead>
              <tbody>
                {walletTxns.map((t) => {
                  const amount = Number(t.amount ?? 0);
                  return (
                    <tr key={t.id} className="border-t border-border/40">
                      <td className="px-3 py-2 text-xs text-muted-foreground tabular-nums">{fmtDate(t.created_at)}</td>
                      <td className="px-3 py-2 text-xs">{txnTypeLabel(t.txn_type)}</td>
                      <td className="px-3 py-2 text-xs">
                        <Badge variant="outline" className="text-[10px]">{t.balance_state ?? "—"}</Badge>
                      </td>
                      <td className={`px-3 py-2 text-end font-semibold tabular-nums ${amount >= 0 ? "text-emerald-600" : "text-destructive"}`}>
                        {amount > 0 ? "+" : ""}{amount.toFixed(2)} {t.currency}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono">{t.related_coupon_code ?? "—"}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground truncate max-w-[260px]">{t.note ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

